import {
  appendSection,
  createSectionSubdocument,
  moveSection as moveManifestSection,
  orderedSectionIds,
  removeSection as removeManifestSection,
  SECTION_SUBDOCS_KEY,
  sectionReferences,
} from "@workith/editor-schema";
import { type CollaborativeUser, sectionRoomName } from "@workith/shared";
import * as Y from "yjs";
import {
  type CollaborationCredentialBroker,
  createCollaborationCredentialBroker,
} from "./collaborationCredentialBroker";
import {
  type CollaborationCloseClassification,
  type CollaborationCloseInfo,
  type CollaborationCredential,
  type CollaborationProvider,
  type CollaborationProviderBundle,
  createCollaborationProvider,
  createCollaborationWebsocket,
} from "./collaborationProvider";
import { buildCollaborationUrl } from "./collaborationUrl";

const SECTION_SYNC_TIMEOUT_MS = 15_000;

export interface SectionedCollaborationUser {
  id: string;
  name: string;
  color: string;
}

/**
 * Effective room names attached to a shared socket anywhere in this tab.
 *
 * `sessionAwareness` is off, so an effective room name is a single slot on the
 * wire: two providers holding the same one would interleave their sync messages
 * instead of multiplexing. React StrictMode makes that reachable in development
 * - the mount effect runs, is cleaned up, and runs again - so an attachment
 * replaces whatever held the name before it rather than joining it.
 */
const attachedRooms = new Map<string, () => void>();

/**
 * Claims an effective room name, detaching whoever held it. The returned
 * function releases the claim, but only if it is still the current one: a slot
 * already taken over by a newer attachment must not be cleared by the old
 * owner's teardown.
 */
function claimRoomName(effectiveRoomName: string, detach: () => void): () => void {
  attachedRooms.get(effectiveRoomName)?.();
  attachedRooms.set(effectiveRoomName, detach);

  return () => {
    if (attachedRooms.get(effectiveRoomName) === detach) {
      attachedRooms.delete(effectiveRoomName);
    }
  };
}

/** Test seam: the effective room names attached right now. */
export function attachedCollaborationRoomNames(): string[] {
  return [...attachedRooms.keys()];
}

/**
 * Document-scoped authorization, aggregated from every room.
 *
 * Only the parent document and the principal live on this axis. A room-local
 * failure (a section that no longer belongs to the document) never moves it,
 * which is what keeps siblings editable - see {@link SectionSnapshot.canEdit}.
 */
export interface SectionedAuthorizationSnapshot {
  /** `pending` until the root room authenticates, then `authorized` or `denied`. */
  readonly state: "pending" | "authorized" | "denied";
  /** Why access ended, once it has. */
  readonly classification: CollaborationCloseClassification | null;
}

/** One room whose pending local changes the server will never accept. */
export interface RejectedChangeRoom {
  /** `null` for the root manifest room. */
  readonly sectionId: string | null;
  readonly roomName: string;
  /** Still live and still holding the refused text; never destroyed here. */
  readonly document: Y.Doc;
  /** Whether the room had unsynced local updates at the moment it was refused. */
  readonly hasPendingChanges: boolean;
}

/**
 * The seam U7's recovery serializer consumes.
 *
 * The controller only captures *which* Y.Docs hold refused content and in what
 * order; turning them into Markdown, offering an export and wiping the payload
 * is U7's job. Capturing is deliberately the first thing that happens on a
 * terminal close, before any room is disabled, because the Y.Doc is the only
 * place the rejected text exists.
 */
export interface RejectedChangeSnapshot {
  /** `document`: the parent was denied, so every loaded room is included. */
  readonly scope: "document" | "section";
  readonly classification: CollaborationCloseClassification;
  readonly capturedAt: number;
  /** Loaded rooms in manifest order, root first. */
  readonly rooms: readonly RejectedChangeRoom[];
}

/** One section room: its manifest subdocument plus the provider syncing it. */
export interface SectionSnapshot {
  sectionId: string;
  document: Y.Doc;
  provider: CollaborationProvider;
  hasSynced: boolean;
  /** This room's own topology, membership or lease ended it. */
  isUnavailable: boolean;
  closeClassification: CollaborationCloseClassification | null;
  /** Whether this section still accepts edits, on the authorization axis. */
  canEdit: boolean;
  hasPendingChanges: boolean;
}

export interface SectionedCollaborationSnapshot {
  isConnected: boolean;
  hasRootSynced: boolean;
  hasConnectionIssue: boolean;
  authorization: SectionedAuthorizationSnapshot;
  /** False once the parent document or principal is denied. */
  canEditDocument: boolean;
  /** Any room - root or section - still holding unaccepted local updates. */
  hasPendingChanges: boolean;
  /** Held for U7; cleared through {@link SectionedCollaborationController.clearRejectedChanges}. */
  rejectedChanges: readonly RejectedChangeSnapshot[];
  /** Sections in manifest order. */
  sections: SectionSnapshot[];
  /** Everyone connected to any room of this document, deduplicated by user id. */
  users: CollaborativeUser[];
}

export interface SectionedCollaborationController {
  readonly documentId: string;
  readonly rootDocument: Y.Doc;
  readonly rootProvider: CollaborationProvider;
  /** The one broker every room of this document renews through. */
  readonly credentialBroker: CollaborationCredentialBroker;
  connect(): Promise<unknown>;
  subscribe(listener: () => void): () => void;
  getSnapshot(): SectionedCollaborationSnapshot;
  /** References an already created section row from the root manifest. */
  insertSection(sectionId: string, options?: { afterSectionId?: string; atIndex?: number }): void;
  /** Stops referencing a section; the row is soft-deleted by the section API. */
  removeSection(sectionId: string): void;
  moveSection(sectionId: string, toIndex: number): void;
  waitForSectionSync(sectionId: string, timeoutMs?: number): Promise<SectionSnapshot>;
  /** One HTTP refresh, fanned out to the root and every section room. */
  refreshCredentials(): Promise<void>;
  /** Releases snapshots U7 has exported or the user dismissed. */
  clearRejectedChanges(): void;
  destroy(): void;
}

export interface CreateSectionedCollaborationControllerOptions {
  baseUrl: string;
  documentId: string;
  tokenProvider: () => Promise<CollaborationCredential>;
  user: SectionedCollaborationUser;
  /**
   * Notified the moment refused local content is captured, before the affected
   * rooms are disabled. U7 hangs its recovery flow here; the same payload also
   * stays readable on the snapshot.
   */
  onRejectedChanges?: (snapshot: RejectedChangeSnapshot) => void;
}

export class LastSectionError extends Error {
  constructor() {
    super("The last section of a document cannot be removed");
    this.name = "LastSectionError";
  }
}

interface SectionEntry {
  sectionId: string;
  document: Y.Doc;
  bundle: CollaborationProviderBundle;
  hasSynced: boolean;
  isUnavailable: boolean;
  closeClassification: CollaborationCloseClassification | null;
  releaseRoomName: () => void;
}

/**
 * Owns every Hocuspocus room of one `sectioned-v1` document.
 *
 * All rooms share a single websocket so a document costs one connection no matter
 * how many sections it has, and every room presents the same parent-scoped token:
 * the server derives the section from the room name and checks DB ownership, so
 * section rights are never minted separately.
 *
 * Providers are reconciled against the root `sections` Y.Array rather than against
 * Yjs subdocument events, because Hocuspocus does not send subdocuments on its
 * own. Reconciliation is keyed by GUID, which is also why a reorder - which
 * replaces the array item with a fresh Y.Doc carrying the same GUID - keeps the
 * existing provider and its bound Y.Doc alive.
 */
export function createSectionedCollaborationController({
  baseUrl,
  documentId,
  tokenProvider,
  user,
  onRejectedChanges,
}: CreateSectionedCollaborationControllerOptions): SectionedCollaborationController {
  const rootDocument = new Y.Doc();
  const websocketProvider = createCollaborationWebsocket(
    buildCollaborationUrl(baseUrl, documentId)
  );
  const entries = new Map<string, SectionEntry>();
  const listeners = new Set<() => void>();

  /**
   * One broker for the whole document. The token is minted per parent document,
   * so root plus N sections cost one HTTP refresh and N+1 in-band sends over the
   * one shared socket (R5) - never one refresh per room.
   */
  const credentialBroker = createCollaborationCredentialBroker({ documentId, tokenProvider });

  let order: string[] = [];
  let hasRootSynced = false;
  let isConnected = false;
  let hasConnectionIssue = false;
  let destroyed = false;
  let reconcileScheduled = false;
  let snapshot: SectionedCollaborationSnapshot | null = null;
  let authorizationState: SectionedAuthorizationSnapshot["state"] = "pending";
  let authorizationClassification: CollaborationCloseClassification | null = null;
  let rejectedChanges: RejectedChangeSnapshot[] = [];

  const notify = () => {
    snapshot = null;
    for (const listener of listeners) listener();
  };

  const applyAwarenessUser = (provider: CollaborationProvider) => {
    provider.setAwarenessField("user", user);
  };

  /** Every room that has content in this tab, root first, then manifest order. */
  function loadedRooms(): RejectedChangeRoom[] {
    const rooms: RejectedChangeRoom[] = [
      {
        sectionId: null,
        roomName: documentId,
        document: rootDocument,
        hasPendingChanges: rootBundle.provider.hasUnsyncedChanges,
      },
    ];

    for (const sectionId of order) {
      const entry = entries.get(sectionId);
      if (entry) rooms.push(rejectedChangeRoom(entry));
    }
    return rooms;
  }

  function rejectedChangeRoom(entry: SectionEntry): RejectedChangeRoom {
    return {
      sectionId: entry.sectionId,
      roomName: entry.bundle.effectiveRoomName,
      document: entry.document,
      hasPendingChanges: entry.bundle.provider.hasUnsyncedChanges,
    };
  }

  function captureRejectedChanges(
    scope: RejectedChangeSnapshot["scope"],
    classification: CollaborationCloseClassification,
    rooms: RejectedChangeRoom[]
  ): void {
    const captured: RejectedChangeSnapshot = {
      scope,
      classification,
      capturedAt: Date.now(),
      rooms,
    };
    // A new array, not a push: the snapshot exposes this list by reference and a
    // mutated array would be indistinguishable from the previous render.
    rejectedChanges = [...rejectedChanges, captured];
    onRejectedChanges?.(captured);
  }

  /**
   * The parent document or the principal lost access, so every room of this
   * document is over. Runs at most once: the server closes the rooms
   * independently and each close would otherwise re-announce the same terminal
   * state and re-capture the same content.
   */
  function denyDocument(info: CollaborationCloseInfo): void {
    if (authorizationState === "denied") return;
    authorizationState = "denied";
    authorizationClassification = info.classification;
    // Captured before anything is disabled: the local Y.Docs are the only place
    // the refused text exists, and U7 reads them from here.
    captureRejectedChanges("document", info.classification, loadedRooms());
    // Terminal means terminal: renewing a revoked credential can only produce a
    // request storm the server will keep refusing (AE5, AE7).
    credentialBroker.suspend();
    notify();
  }

  /**
   * This one section's room ended - it no longer belongs to the document, or its
   * own lease/room budget ran out. Siblings and the root keep their rooms.
   */
  function disableSection(sectionId: string, info: CollaborationCloseInfo): void {
    const entry = entries.get(sectionId);
    if (!entry || entry.isUnavailable) return;

    // Snapshot first, disable second. Reversing this would leave U7 with a
    // section that is already read-only and no record of what it refused.
    captureRejectedChanges("section", info.classification, [rejectedChangeRoom(entry)]);
    entry.isUnavailable = true;
    entry.closeClassification = info.classification;
    notify();
  }

  function handleRoomClose(sectionId: string | null, info: CollaborationCloseInfo): void {
    if (info.scope === "document") {
      denyDocument(info);
      return;
    }
    if (info.scope !== "room") return;

    // A room-scoped close on the root room is still the whole document: without
    // the manifest there is no section to keep editing, so it fails closed.
    if (sectionId === null) {
      denyDocument(info);
      return;
    }
    disableSection(sectionId, info);
  }

  const rootBundle = createCollaborationProvider({
    documentId,
    websocketProvider,
    document: rootDocument,
    credentialBroker,
    onDisconnect: () => {
      isConnected = false;
      notify();
    },
    onClose: (info) => handleRoomClose(null, info),
    onAuthenticated: (provider) => {
      applyAwarenessUser(provider);
      if (authorizationState === "pending") {
        authorizationState = "authorized";
        notify();
      }
    },
    onSynced: () => {
      hasRootSynced = true;
      isConnected = true;
      hasConnectionIssue = false;
      scheduleReconcile();
      notify();
    },
    onAwarenessChange: notify,
    onConnectionTimeout: () => {
      hasConnectionIssue = true;
      isConnected = false;
      notify();
    },
  });
  const releaseRootRoomName = claimRoomName(rootBundle.effectiveRoomName, rootBundle.detach);

  function createSectionEntry(sectionId: string, document: Y.Doc): SectionEntry {
    const bundle = createCollaborationProvider({
      documentId,
      roomName: sectionRoomName(documentId, sectionId),
      websocketProvider,
      // The exact object held by the manifest Y.Array, never a fresh Y.Doc.
      document,
      // The same broker as the root: a section presents the parent token and
      // never mints one of its own.
      credentialBroker,
      onDisconnect: () => {
        isConnected = false;
        notify();
      },
      onClose: (info) => handleRoomClose(sectionId, info),
      onAuthenticated: applyAwarenessUser,
      onSynced: () => {
        const entry = entries.get(sectionId);
        if (!entry) return;
        entry.hasSynced = true;
        notify();
      },
      onAwarenessChange: notify,
    });

    const entry: SectionEntry = {
      sectionId,
      document,
      bundle,
      hasSynced: false,
      isUnavailable: false,
      closeClassification: null,
      releaseRoomName: claimRoomName(bundle.effectiveRoomName, bundle.detach),
    };
    entries.set(sectionId, entry);
    bundle.connect().catch((error) => {
      console.error("❌ Failed to join section room", { sectionId, error });
    });
    return entry;
  }

  function destroySectionEntry(entry: SectionEntry): void {
    entry.releaseRoomName();
    entry.bundle.destroy();
  }

  /**
   * Brings the provider map in line with the manifest. Runs outside the Yjs
   * transaction that triggered it so provider construction never nests inside an
   * observer callback.
   */
  function reconcile(): void {
    if (destroyed) return;

    // The manifest is walked once into a GUID index. Looking each section up by
    // materializing the Y.Array per section made the initial sync of an N-section
    // document quadratic, and reorders paid the same cost again.
    const manifestDocuments = new Map(
      sectionReferences(rootDocument)
        .toArray()
        .map((section) => [section.guid, section])
    );
    const manifestOrder = [...manifestDocuments.keys()];

    for (const sectionId of manifestOrder) {
      if (entries.has(sectionId)) continue;
      const document = manifestDocuments.get(sectionId);
      if (!document) continue;
      createSectionEntry(sectionId, document);
    }

    for (const [sectionId, entry] of entries) {
      if (manifestDocuments.has(sectionId)) continue;
      entries.delete(sectionId);
      destroySectionEntry(entry);
    }

    order = manifestOrder;
    notify();
  }

  function scheduleReconcile(): void {
    if (destroyed || reconcileScheduled) return;
    reconcileScheduled = true;
    queueMicrotask(() => {
      reconcileScheduled = false;
      reconcile();
    });
  }

  const observeManifest = () => scheduleReconcile();
  rootDocument.getArray<Y.Doc>(SECTION_SUBDOCS_KEY).observe(observeManifest);

  function collectUsers(): CollaborativeUser[] {
    const byIdentity = new Map<string, CollaborativeUser>();
    const providers = [rootBundle.provider, ...[...entries.values()].map((e) => e.bundle.provider)];

    for (const provider of providers) {
      const states = provider.awareness?.getStates() as
        | Map<number, Record<string, unknown>>
        | undefined;
      if (!states) continue;

      for (const [clientId, state] of states) {
        const awarenessUser = state?.user as
          | { id?: string; name: string; color: string }
          | undefined;
        if (!awarenessUser) continue;

        // One person editing three sections is one document participant.
        const identity = awarenessUser.id ?? `client:${clientId}`;
        if (byIdentity.has(identity)) continue;
        byIdentity.set(identity, {
          clientId,
          user: { name: awarenessUser.name, color: awarenessUser.color },
          cursor: state?.cursor as CollaborativeUser["cursor"],
        });
      }
    }

    return [...byIdentity.values()];
  }

  /**
   * Recomputed only when something notified, and cached until then. React reads
   * this on every render, so a fresh object per call would be an update loop.
   */
  function getSnapshot(): SectionedCollaborationSnapshot {
    if (snapshot) return snapshot;

    const canEditDocument = authorizationState !== "denied";
    const sections = order.flatMap<SectionSnapshot>((sectionId) => {
      const entry = entries.get(sectionId);
      if (!entry) return [];
      return [
        {
          sectionId,
          document: entry.document,
          provider: entry.bundle.provider,
          hasSynced: entry.hasSynced,
          isUnavailable: entry.isUnavailable,
          closeClassification: entry.closeClassification,
          canEdit: canEditDocument && !entry.isUnavailable,
          hasPendingChanges: entry.bundle.provider.hasUnsyncedChanges,
        },
      ];
    });

    snapshot = {
      isConnected,
      hasRootSynced,
      hasConnectionIssue,
      authorization: { state: authorizationState, classification: authorizationClassification },
      canEditDocument,
      hasPendingChanges:
        rootBundle.provider.hasUnsyncedChanges ||
        sections.some((section) => section.hasPendingChanges),
      rejectedChanges,
      sections,
      users: collectUsers(),
    };
    return snapshot;
  }

  return {
    documentId,
    rootDocument,
    rootProvider: rootBundle.provider,
    credentialBroker,
    connect: () => rootBundle.connect(),

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    getSnapshot,

    insertSection(sectionId, options) {
      if (orderedSectionIds(rootDocument).includes(sectionId)) return;
      const subdocument = createSectionSubdocument(sectionId);
      const afterSectionId = options?.afterSectionId;
      const atIndex = options?.atIndex;

      if (atIndex !== undefined) {
        const sections = sectionReferences(rootDocument);
        sections.insert(Math.max(0, Math.min(atIndex, sections.length)), [subdocument]);
      } else if (afterSectionId === undefined) {
        appendSection(rootDocument, subdocument);
      } else {
        const sections = sectionReferences(rootDocument);
        const afterIndex = orderedSectionIds(rootDocument).indexOf(afterSectionId);
        sections.insert(afterIndex === -1 ? sections.length : afterIndex + 1, [subdocument]);
      }
      scheduleReconcile();
    },

    removeSection(sectionId) {
      if (orderedSectionIds(rootDocument).length <= 1) {
        throw new LastSectionError();
      }
      removeManifestSection(rootDocument, sectionId);
      scheduleReconcile();
    },

    moveSection(sectionId, toIndex) {
      moveManifestSection(rootDocument, sectionId, toIndex);
      scheduleReconcile();
    },

    waitForSectionSync(sectionId, timeoutMs = SECTION_SYNC_TIMEOUT_MS) {
      return new Promise<SectionSnapshot>((resolve, reject) => {
        const check = () => {
          const section = getSnapshot().sections.find(
            (candidate) => candidate.sectionId === sectionId
          );
          if (!section?.hasSynced) return false;
          cleanup();
          resolve(section);
          return true;
        };

        const timer = setTimeout(() => {
          cleanup();
          reject(new Error(`Section ${sectionId} did not sync in ${timeoutMs}ms`));
        }, timeoutMs);

        const unsubscribe = this.subscribe(() => {
          check();
        });

        function cleanup() {
          clearTimeout(timer);
          unsubscribe();
        }

        check();
      });
    },

    refreshCredentials: () => credentialBroker.refreshAndFanOut("manual"),

    clearRejectedChanges() {
      if (rejectedChanges.length === 0) return;
      rejectedChanges = [];
      notify();
    },

    destroy() {
      if (destroyed) return;
      destroyed = true;

      rootDocument.getArray<Y.Doc>(SECTION_SUBDOCS_KEY).unobserve(observeManifest);
      for (const entry of entries.values()) {
        destroySectionEntry(entry);
      }
      entries.clear();
      order = [];
      releaseRootRoomName();
      rootBundle.destroy();
      // Every room renewed through this broker, so it is torn down with them.
      credentialBroker.destroy();
      // The shared socket is owned here, so it is closed exactly once.
      websocketProvider.destroy();
      listeners.clear();
      rejectedChanges = [];
      snapshot = null;
    },
  };
}
